import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { Dispatch, SetStateAction } from "react";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { Card } from "shared/components/Card";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "shared/types";
import { ApplicationStatus, Money } from "shared/types/application";
import { formatToCurrency } from "shared/utils";
import { Property } from "types/landlord/address";
import { ApplicationRentSection } from "views/applications-id/components/LayoutHeader/components/ApplicationRentSection";
import { Stat } from "./components/Stat";

export const ApplicationOverview = ({
  applicationId,
  applicationRentAmount,
  setShowActivityLog,
  applicationStatus,
  latestApplicationRentHistory,
  unit,
  property,
  desiredMoveInDate
}: {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  setShowActivityLog: Dispatch<SetStateAction<boolean>>;
  applicationStatus: ApplicationStatus;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  unit: Unit;
  property: Property;
  desiredMoveInDate?: string;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { permissions, pms } = useAuthContext();
  const queryClient = useQueryClient();

  return (
    <Card className="w-full">
      <div className="border-b border-alpha-40 px-6 py-4">
        <ApplicationRentSection
          applicationId={applicationId}
          applicationRentAmount={applicationRentAmount}
          onViewDetails={() => setShowActivityLog(true)}
          canEdit={
            permissions.canEditApplicationRent &&
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              ApplicationStatus.Processing,
              ApplicationStatus.Submitted
            ].includes(applicationStatus)
          }
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }}
          latestApplicationRentHistory={latestApplicationRentHistory}
        />
      </div>

      <div className="w-full [@media(min-width:1695px)]:flex block">
        {/* Property Information Section */}
        <div className="flex items-center gap-6 p-6 flex-wrap">
          <Stat
            label={`${dictionary.Property} name`}
            value={property.name || "--"}
          />
          <Stat
            label={`${dictionary.Property}`}
            value={`${property.address.address1}`}
          />
          <Stat label={`${dictionary.Unit}`} value={`${unit.title || "-"}`} />
          <Stat
            label={`${dictionary.Property} group`}
            value={property.property_group?.name || "--"}
          />
        </div>

        <div className="flex flex-wrap">
          {/* Rent Information Section */}
          <div className="flex items-center gap-6 p-6">
            {!pms.isManageAmerica ? (
              <Stat
                label="Listed rent"
                value={formatToCurrency({
                  cents: unit.monthly_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              />
            ) : (
              <>
                <Stat
                  label="Listed site rent"
                  value={formatToCurrency({
                    cents: unit.site_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                />

                <Stat
                  label="Listed home rent"
                  value={formatToCurrency({
                    cents: unit.home_rent_amount?.cents || 0,
                    currency: "USD"
                  })}
                />
              </>
            )}
          </div>

          {/* Date Information Section */}
          <div className="flex items-center gap-6 p-6">
            <Stat
              label="Available"
              value={
                unit.available_at && moment(unit.available_at).isValid()
                  ? moment(unit.available_at).utc().format("MMM D, YYYY")
                  : "--"
              }
            />
            <Stat
              label="Desired move-in"
              value={
                desiredMoveInDate && moment(desiredMoveInDate).isValid()
                  ? moment(desiredMoveInDate).utc().format("MMM D, YYYY")
                  : "--"
              }
            />
          </div>
        </div>
      </div>
    </Card>
  );
};
